import * as d3 from 'd3';
import { interpolateSinebow } from 'd3-scale-chromatic';
import { BaseChart, UIState } from '../../js/classes/BaseChart.ts';
import { CwcDataSet } from '../../js/generated/cwc_data.pb.js';

type D3Selection<T extends d3.BaseType> = d3.Selection<T, unknown, null, undefined>;

interface PointData {
  elapsedMonths: number;
  value: number;
}
interface LineSeries {
  key: string;
  name: string;
  color: string;
  points: PointData[];
  maxMonths: number;
}

interface ConsoleWarsUIState extends UIState {
  currentDate: null; 
}

interface GameConsoleMetaData {
  timeline: string[];
  consoles: { [key: string]: ConsoleInfo };
  seriesOrder: string[];
}
interface ConsoleInfo {
  name: string;
  releaseDate?: string;
}

export class ConsoleSalesOrigin extends BaseChart<CwcDataSet, LineSeries, ConsoleWarsUIState> {
  static override defaultConfig = {
    defaultVisibleKeys: ['FC', 'SFC', 'N64', 'GC', 'Wii', 'WiiU', 'NS', 'NS2'],
    animationDurationPerSeries: 3000, 
    lineWidth: 2.5,
    gridLineWidth: 1,
    highlightOpacity: 1.0,
    dimmedOpacity: 0.15,
    xAxisTickIntervalMonths: 24,
    yAxisTicks: 5,
    yAxisTickPadding: 8,
    axisLabelOffsetY: 20,
    tooltipOffsetX: -100,
    tooltipOffsetY: -100,
  };

  private lineData: LineSeries[] = [];
  private seriesOrder: string[] = [];
  private visibleSeriesKeys: Set<string>;

  protected override fullDataSet: CwcDataSet | null = null;
  private mappingData: GameConsoleMetaData | null = null;

  private gridGroup: D3Selection<SVGGElement> | null = null;
  private linesGroup: D3Selection<SVGGElement> | null = null;
  private markersGroup: D3Selection<SVGGElement> | null = null; 
  
  private xScale: d3.ScaleLinear<number, number> | null = null;
  private yScale: d3.ScaleLinear<number, number> | null = null;
  private colorScale: d3.ScaleOrdinal<string, string> | null = null;
  private lineGenerator: d3.Line<PointData> | null = null;
  private tooltip: D3Selection<HTMLDivElement> | null = null;
  private hoverGridLinesGroup: d3.Selection<SVGGElement, unknown, null, undefined> | null = null;
  private highlightedKey: string | null = null;

  private lineLengths: Map<string, number> = new Map();
  
  private cachedPaths: D3Selection<SVGPathElement> | null = null;

  constructor(chartElement: HTMLElement, config = {}) {
    super(chartElement, config);
    this.visibleSeriesKeys = new Set(this.config.defaultVisibleKeys);
    this.margin = { top: 20, right: 20, bottom: 50, left: 70 };
  }

  public async loadDataForUI(): Promise<UiDataForInitializer | null> {
    if (this.mappingData) {
      return { seriesOrder: this.mappingData.seriesOrder, consoles: this.mappingData.consoles };
    }
    const mappingUrl = (this.chartElement as HTMLElement).dataset.mappingJsonSourceUrl;
    const protoUrl = (this.chartElement as HTMLElement).dataset.protobufSourceUrl;

    if (!mappingUrl || !protoUrl) {
      this._displayMessage('データソースのURLが指定されていません。', 'error');
      return null;
    }

    try {
      const [protoResponse, mappingResponse] = await Promise.all([
        fetch(protoUrl),
        fetch(mappingUrl)
      ]);
      if (!protoResponse.ok) throw new Error(`Protobufの読み込みに失敗: ${protoResponse.statusText}`);
      if (!mappingResponse.ok) throw new Error(`マッピングJSONの読み込みに失敗: ${mappingResponse.statusText}`);

      const [protoBuffer, mappingJson] = await Promise.all([
        protoResponse.arrayBuffer(),
        mappingResponse.json()
      ]);

      const decodedMessage = CwcDataSet.decode(new Uint8Array(protoBuffer));
      this.fullDataSet = CwcDataSet.toObject(decodedMessage, { defaults: true }) as CwcDataSet;
      this.mappingData = mappingJson.meta as GameConsoleMetaData;

      return { seriesOrder: this.mappingData.seriesOrder, consoles: this.mappingData.consoles };
    } catch (error) {
      console.error('ConsoleSalesOrigin CRITICAL ERROR:', error);
      this._displayMessage('データファイルの読み込みに失敗しました。', 'error');
      return null;
    }
  }

  public override async init(): Promise<void> {
    if (!this.fullDataSet) {
      await this.loadDataForUI();
    }
    await super.init();
  }

  public setVisibleSeries(visibleKeys: Set<string>): void {
    this.visibleSeriesKeys = visibleKeys;
    this.resetAndRedraw();
  }

  protected override setupDimensions(): void {
    super.setupDimensions();
    this.width = Math.floor(this.width);
    this.height = Math.floor(this.height);
  }

  protected override _processData(): void {
    if (!this.fullDataSet || !this.mappingData) return;
    const mappingData = this.mappingData;
    const fullDataSet = this.fullDataSet;

    this.seriesOrder = mappingData.seriesOrder;

    this.colorScale = d3.scaleOrdinal<string, string>()
      .domain(this.seriesOrder)
      .range(this.seriesOrder.map((_, i) => interpolateSinebow(i / (this.seriesOrder.length - 1))));

    this.lineData = fullDataSet.series.reduce<LineSeries[]>((acc, seriesProto) => {
      const id = seriesProto.id;
      const values = seriesProto.values;
      if (id == null || !Array.isArray(values) || id < 0 || id >= this.seriesOrder.length) {
        return acc;
      }
      const key = this.seriesOrder[id];
      
      const points: PointData[] = values.map((value, i) => {
        return { elapsedMonths: i, value };
      });
      
      acc.push({
        key: key,
        name: mappingData.consoles[key]?.name || key,
        color: this.colorScale!(key),
        points,
        maxMonths: points.length - 1
      });
      return acc;
    }, []);
  }

  protected override resetAndRedraw(): void {
    this.pause();

    this.activeLineData = this.seriesOrder
      .filter(key => this.visibleSeriesKeys.has(key))
      .map(key => this.lineData.find(d => d.key === key))
      .filter((d): d is LineSeries => !!d);

    const maxMonths = d3.max(this.activeLineData, d => d.maxMonths) || 0;
    this.totalFrames = maxMonths;
    
    this.currentFrame = 0;

    this._initialDrawAxes();

    if (this.activeLineData.length === 0) {
      this.linesGroup?.selectAll('*').remove();
      this.markersGroup?.selectAll('*').remove();
      this.updateUI();
      return;
    }

    this.isReady = true;
    
    this.drawFrame(0);
    this.updateUI();

    if (this.config.autoplay) {
      setTimeout(() => this.play(), this.config.autoplayDelay);
    }
  }

  public override play(): void {
    if (this.isPlaying || !this.isReady || !this.g) return;
    
    if (this.currentFrame >= this.totalFrames) {
      this.currentFrame = 0;
    }

    this.isPlaying = true;
    this.updateUI();

    const remainingRatio = 1 - (this.currentFrame / (this.totalFrames || 1));
    const duration = this.config.animationDurationPerSeries * remainingRatio;

    const interpolator = d3.interpolate(this.currentFrame, this.totalFrames);

    this.g.transition()
      .duration(duration)
      .ease(d3.easeLinear)
      .tween("frameTween", () => {
        return (t: number) => {
          this.currentFrame = interpolator(t);
          this.drawFrame(this.currentFrame);
          this.updateUI();
        };
      })
      .on("end", () => {
        this.isPlaying = false;
        this.currentFrame = this.totalFrames;
        this.drawFrame(this.currentFrame);
        this.updateUI();
      });
  }

  public override pause(): void {
    if (!this.g) return;
    this.isPlaying = false;
    this.g.interrupt();
    this.updateUI();
  }

  public override seek(frame: number): void {
    if (!this.isReady) return;
    this.pause();
    this.currentFrame = Math.max(0, Math.min(frame, this.totalFrames));
    this.drawFrame(this.currentFrame);
    this.updateUI();
  }

  protected override setupLayers(): void {
    d3.select(this.chartElement).selectAll('*').remove();
    
    const totalWidth = this.width + this.margin.left + this.margin.right;
    const totalHeight = this.height + this.margin.top + this.margin.bottom;
    
    this.svg = d3.select(this.chartElement).append('svg')
      .attr('width', totalWidth).attr('height', totalHeight)
      .attr('viewBox', `0 0 ${totalWidth} ${totalHeight}`);
    
    this.g = this.svg.append('g').attr('transform', `translate(${this.margin.left},${this.margin.top})`);
    
    this.gridGroup = this.g.append('g').attr('class', 'grid-group');
    this.linesGroup = this.g.append('g').attr('class', 'lines-group');
    
    const wrapper = this.chartElement.closest<HTMLElement>('.chart-container-wrapper');
    if (wrapper) {
      let tooltip = d3.select(wrapper).select<HTMLDivElement>('.chart-tooltip');
      if (tooltip.empty()) {
        tooltip = d3.select(wrapper).append('div').attr('class', 'chart-tooltip');
      }
      this.tooltip = tooltip;
    }
  }

  protected override _initialDrawAxes(): void {
    if (!this.g || !this.mappingData) return;
    
    this.g.selectAll('.axis').remove();
    this.g.selectAll('.axis-label').remove();
    this.g.selectAll('.markers-group').remove();
    this.g.selectAll('.hover-grid-lines').remove();

    this.hoverGridLinesGroup = this.g.append('g').attr('class', 'hover-grid-lines');

    const xMax = d3.max(this.activeLineData, d => d.maxMonths) || 0;
    this.xScale = d3.scaleLinear().domain([0, xMax]).range([0, this.width]);
    
    const yMax = d3.max(this.activeLineData, s => d3.max(s.points, p => p.value)) || 0;
    this.yScale = d3.scaleLinear().domain([0, yMax]).range([this.height, 0]).nice();
    
    if (this.gridGroup && this.yScale) {
        this.gridGroup.selectAll('*').remove();
        const gridTicks = this.yScale.ticks(this.config.yAxisTicks);
        this.gridGroup.selectAll('line.grid-line')
            .data(gridTicks)
            .enter()
            .append('line')
            .attr('class', 'grid-line')
            .attr('x1', 0)
            .attr('x2', this.width)
            .attr('y1', d => this.yScale!(d))
            .attr('y2', d => this.yScale!(d))
            .style('stroke', '#e9e9e9')
            .style('stroke-width', this.config.gridLineWidth)
            .style('shape-rendering', 'crispEdges');
    }

    this.lineGenerator = d3.line<PointData>()
        .x(d => this.xScale!(d.elapsedMonths))
        .y(d => this.yScale!(d.value));

    this.lineLengths.clear();
    this.cachedPaths = null;

    if (this.linesGroup) {
      this.linesGroup.selectAll('*').remove();
      
      this.activeLineData.forEach(series => {
        const pathData = this.lineGenerator!(series.points);
        if (!pathData) return;

        const pathElement = this.linesGroup!.append('path')
          .attr('class', 'line-path')
          .attr('data-key', series.key)
          .attr('d', pathData)
          .style('fill', 'none')
          .style('stroke', series.color)
          .style('stroke-width', this.config.lineWidth)
          .style('stroke-linecap', 'round')
          .style('stroke-linejoin', 'round')
          .style('opacity', 0);

        const length = pathElement.node()?.getTotalLength() || 0;
        this.lineLengths.set(series.key, length);

        pathElement
          .style('stroke-dasharray', length)
          .style('stroke-dashoffset', length);
      });

      this.cachedPaths = this.linesGroup.selectAll<SVGPathElement, unknown>('.line-path') as unknown as D3Selection<SVGPathElement>;
    }

    const yearTicks = d3.range(0, xMax + 1, this.config.xAxisTickIntervalMonths);
    const xAxis = d3.axisBottom(this.xScale)
        .tickValues(yearTicks)
        .tickFormat(d => `${(d as number) / 12}年`)
        .tickSizeOuter(0);

    this.g.append('g').attr('class', 'x-axis axis').attr('transform', `translate(0, ${this.height})`).call(xAxis);
    
    const yAxis = d3.axisLeft(this.yScale)
      .ticks(this.config.yAxisTicks)
      .tickFormat(d => `${(d as number) / 1_000_000}M`)
      .tickPadding(this.config.yAxisTickPadding);
    this.g.append('g').attr('class', 'y-axis axis').call(yAxis);
    
    this.markersGroup = this.g.append('g').attr('class', 'markers-group');
    this.g.append('text').attr('class', 'axis-label').attr('transform', 'rotate(-90)').attr('y', 0 - this.margin.left).attr('x', 0 - this.height / 2).attr('dy', '1em').text('普及台数');
    this.g.append('text').attr('class', 'axis-label').attr('transform', `translate(${this.width / 2}, ${this.height + this.margin.top + this.config.axisLabelOffsetY})`).text('販売期間');
  }

  protected override async drawFrame(currentGlobalMonths: number): Promise<void> {
    
    if (this.cachedPaths) {
      const lineLengths = this.lineLengths;
      const activeData = this.activeLineData;

      this.cachedPaths.style('stroke-dashoffset', function() {
         const key = this.getAttribute('data-key');
         if (!key) return 0;
         
         const series = activeData.find(s => s.key === key);
         const totalLength = lineLengths.get(key) || 0;
         if (!series) return totalLength;

         let progress = 0;
         if (currentGlobalMonths <= 0) {
             progress = 0;
         } else if (currentGlobalMonths >= series.maxMonths) {
             progress = 1;
         } else {
             progress = currentGlobalMonths / series.maxMonths;
         }
         
         return totalLength * (1 - progress);
      });
      
      this._applyHighlightStyles();
    }

    this._updateMarkers(currentGlobalMonths);
  }

  private _applyHighlightStyles(): void {
    if (!this.cachedPaths) return;
    const isHighlighting = this.highlightedKey !== null;
    const highlightedKey = this.highlightedKey;
    const config = this.config;
    
    const isStart = this.currentFrame <= 0.01;

    this.cachedPaths
      .style('stroke-width', function() {
        const key = this.getAttribute('data-key');
        return key === highlightedKey ? config.lineWidth * 1.5 : config.lineWidth;
      })
      .style('opacity', function() {
         if (isStart) return 0;

         if (!isHighlighting) return 1.0; 
         const key = this.getAttribute('data-key');
         return key === highlightedKey ? config.highlightOpacity : config.dimmedOpacity;
      });
  }

  private _updateMarkers(currentGlobalMonths: number): void {
    if (!this.markersGroup || !this.xScale || !this.yScale || !this.mappingData) return;
    
    if (currentGlobalMonths < this.totalFrames - 1) {
      this.markersGroup.selectAll('*').remove();
      return;
    }
    
    if (!this.markersGroup.selectAll('.endpoint-marker').empty()) {
        return; 
    }

    this.markersGroup.selectAll('*').remove();
    this.activeLineData.forEach(series => {
      const lastPoint = series.points[series.points.length - 1];
      if (lastPoint) {
        const cx = this.xScale!(lastPoint.elapsedMonths);
        const cy = this.yScale!(lastPoint.value);
        
        const consoleInfo = this.mappingData!.consoles[series.key];
        const consoleName = consoleInfo?.name || series.key.toUpperCase();

        this.markersGroup!.append('circle')
          .attr('class', 'endpoint-marker')
          .attr('cx', cx).attr('cy', cy).attr('r', 5).style('fill', series.color)
          .attr('data-key', series.key).attr('data-name', consoleName)
          .attr('data-value', lastPoint.value).attr('data-months', lastPoint.elapsedMonths)
          .on('mouseover', (event: MouseEvent) => {
            if (this.isPlaying) return;
            const target = d3.select(event.currentTarget as SVGCircleElement);
            target.classed('is-hovered', true).raise();
            this.highlightedKey = target.attr('data-key');
            
            this._applyHighlightStyles();
            
            this.hoverGridLinesGroup?.selectAll('*').remove();
            this.hoverGridLinesGroup?.append('line').attr('x1', cx).attr('y1', cy).attr('x2', cx).attr('y2', this.height);
            this.hoverGridLinesGroup?.append('line').attr('x1', cx).attr('y1', cy).attr('x2', 0).attr('y2', cy);
            this.tooltip?.classed('is-visible', true);
          })
          .on('mousemove', (event: MouseEvent) => {
            if (this.isPlaying || !this.tooltip) return;
            const target = d3.select(event.currentTarget as SVGCircleElement);
            const key = target.attr('data-key');
            if (!key) return;

            const consoleData = this.mappingData?.consoles[key];
            const name = consoleData?.name || key.toUpperCase();
            // ★追加: 発売日の取得
            const releaseDate = consoleData?.releaseDate;
            
            const value = parseFloat(target.attr('data-value')).toLocaleString();
            const months = target.attr('data-months');

            let tooltipHtml = `<div class="tooltip-name">${name}</div>`;
            // ★追加: 発売日があれば表示する
            if (releaseDate) {
              tooltipHtml += `<div class="tooltip-release-date">発売日: ${releaseDate}</div>`;
            }
            tooltipHtml += `<div class="tooltip-value">${value} <span class="unit">units</span></div>`;
            tooltipHtml += `<div class="tooltip-months">${months} <span class="unit">months</span></div>`;

            this.tooltip.html(tooltipHtml);

            const wrapper = this.chartElement.closest('.chart-container-wrapper');
            if (wrapper) {
              const [mouseX, mouseY] = d3.pointer(event, wrapper);
              this.tooltip.style('left', `${mouseX + this.config.tooltipOffsetX}px`)
                .style('top', `${mouseY + this.config.tooltipOffsetY}px`);
            }
          })
          .on('mouseout', (event: MouseEvent) => {
            if (this.isPlaying) return;
            d3.select(event.currentTarget as SVGCircleElement).classed('is-hovered', false);
            this.highlightedKey = null;
            
            this._applyHighlightStyles();
            
            this.hoverGridLinesGroup?.selectAll('*').remove();
            this.tooltip?.classed('is-visible', false);
          });
      }
    });
  }

  protected override updateUI(): void {
    if (typeof this.onUpdateUICallback === 'function') {
      const state: ConsoleWarsUIState = {
        isPlaying: this.isPlaying,
        currentFrame: Math.floor(this.currentFrame),
        totalFrames: Math.floor(this.totalFrames),
        currentDate: null
      };
      this.onUpdateUICallback(state);
    }
  }
}